import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUpload, FiFileText, FiLoader } from 'react-icons/fi';
import Button from '../common/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../common/Card';
import { getJdList } from '../../services/jdService';
import { submitEvaluation } from '../../services/evaluationService';
import { extractTextFromFile, validateResumeFile } from '../../utils/fileExtractor';

const EvaluationForm = () => {
  const [jds, setJds] = useState([]);
  const [selectedJdId, setSelectedJdId] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingJds, setLoadingJds] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  // 加载JD列表
  useEffect(() => {
    const fetchJds = async () => {
      try {
        const response = await getJdList(1, 100);
        setJds(response.items);
      } catch (err) {
        setError('加载JD列表失败');
        console.error(err);
      } finally {
        setLoadingJds(false);
      }
    };

    fetchJds();
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // 验证文件
        validateResumeFile(file);
        
        setResumeFile(file);
        setResumeText('');
        setError('');
        
        // 开始提取文本
        setExtracting(true);
        const text = await extractTextFromFile(file);
        setResumeText(text);
        
      } catch (err) {
        setError(err.message || '文件格式不支持或文件损坏');
        setResumeFile(null);
        setResumeText('');
        e.target.value = null;
      } finally {
        setExtracting(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedJdId) {
      setError('请选择一个JD');
      return;
    }

    if (!resumeFile) {
      setError('请上传简历文件');
      return;
    }

    if (!resumeText) {
      setError('文件处理失败，请重新上传文件');
      return;
    }

    setLoading(true);

    try {
      console.log('提交评估参数:', { jdId: selectedJdId, fileName: resumeFile.name });
      const response = await submitEvaluation(selectedJdId, resumeText);
      console.log('评估提交成功:', response);
      navigate(`/evaluations/${response.id}`);
    } catch (err) {
      console.error('评估提交失败:', err);
      setError(err.message || '提交评估失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>评估简历</CardTitle>
        <CardDescription>
          选择一个职位描述并上传简历进行匹配度评估
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="jd" className="text-sm font-medium">
              选择职位描述
            </label>
            {loadingJds ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-muted-foreground">加载中...</span>
              </div>
            ) : jds.length > 0 ? (
              <select
                id="jd"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedJdId}
                onChange={(e) => setSelectedJdId(e.target.value)}
                required
              >
                <option value="">-- 请选择职位描述 --</option>
                {jds.map((jd) => (
                  <option key={jd.id} value={jd.id}>
                    {jd.title}
                  </option>
                ))}
              </select>
            ) : (
              <div className="p-3 rounded-md bg-accent text-sm">
                暂无职位描述，请先在"管理JD"页面添加
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="resume" className="text-sm font-medium">
              上传简历
            </label>
            <div className="border-2 border-dashed border-input rounded-md p-6 text-center">
              <input
                id="resume"
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                className="hidden"
              />
              {resumeFile ? (
                <div className="flex flex-col items-center gap-2">
                  {extracting ? (
                    <div className="flex flex-col items-center gap-2">
                      <FiLoader className="h-8 w-8 text-primary animate-spin" />
                      <span className="text-sm text-muted-foreground">正在处理文件...</span>
                    </div>
                  ) : (
                    <>
                      <FiFileText className="h-8 w-8 text-primary" />
                      <span className="text-sm font-medium">{resumeFile.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('resume').click()}
                      >
                        更换文件
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <FiUpload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    点击上传或拖放文件到此处
                  </p>
                  <p className="text-xs text-muted-foreground">
                    支持PDF和DOCX格式，最大10MB
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('resume').click()}
                  >
                    选择文件
                  </Button>
                </div>
              )}
            </div>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={loading || extracting || !selectedJdId || !resumeFile || !resumeText}
        >
          {loading ? '提交中...' : '开始评估'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default EvaluationForm;
